import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { PermissionGate } from "../../../components/auth/PermissionGate";
import { SoftDataGrid } from "../../../components/data/SoftDataGrid";

import { getWifiPackages } from "../../../services/wifiPackagesService";

import { ShopWifiPackageRuleForm } from "../components/ShopWifiPackageRuleForm";
import { BulkShopWifiPackageRulesForm } from "../components/BulkShopWifiPackageRulesForm";

import {
  createShopWifiPackageRule,
  deleteShopWifiPackageRule,
  getShopWifiPackageRule,
  getShopWifiPackageRules,
  updateShopWifiPackageRule,
} from "../../../services/shopWifiPackageRulesService";

import { getApiErrorMessage } from "../../../utils/apiData";

const fieldSx = {
  "& .MuiInputBase-root": {
    minHeight: 38,
    borderRadius: 0,
    fontSize: "0.72rem",
  },

  "& .MuiInputLabel-root": {
    fontSize: "0.72rem",
  },

  "& .MuiFormHelperText-root": {
    mx: 0,
    fontSize: "0.63rem",
  },
};

function extractRows(
  payload,
  key,
) {
  const paginator =
    payload?.[key] ??
    payload?.data?.[key] ??
    payload?.data ??
    payload;

  if (
    Array.isArray(
      paginator?.data,
    )
  ) {
    return paginator.data;
  }

  if (
    Array.isArray(
      paginator,
    )
  ) {
    return paginator;
  }

  return [];
}

function extractTotal(
  payload,
  key,
) {
  const paginator =
    payload?.[key] ??
    payload?.data?.[key] ??
    payload?.data ??
    payload;

  return (
    paginator?.total ??
    extractRows(
      payload,
      key,
    ).length
  );
}

function initials(
  value,
) {
  return String(
    value || "WR",
  )
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(
      (part) =>
        part[0],
    )
    .join("")
    .toUpperCase();
}

function money(
  value,
  currency,
) {
  try {
    return new Intl.NumberFormat(
      "en-ZA",
      {
        style:
          "currency",

        currency:
          currency ||
          "USD",

        minimumFractionDigits:
          2,
      },
    ).format(
      Number(
        value || 0,
      ),
    );
  } catch {
    return `${
      currency || "USD"
    } ${Number(
      value || 0,
    ).toFixed(2)}`;
  }
}

function SquareDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  maxWidth = "md",
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={
        maxWidth
      }
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom:
            "1px solid #E3E7EC",
        }}
      >
        <Typography
          sx={{
            fontSize:
              "0.92rem",

            fontWeight:
              850,
          }}
        >
          {title}
        </Typography>

        {subtitle ? (
          <Typography
            sx={{
              mt: 0.25,

              color:
                "#667085",

              fontSize:
                "0.67rem",
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </DialogTitle>

      <DialogContent
        sx={{
          p: 2.2,

          bgcolor:
            "#FBFCFD",
        }}
      >
        {children}
      </DialogContent>

      {actions ? (
        <DialogActions
          sx={{
            borderTop:
              "1px solid #E3E7EC",
          }}
        >
          {actions}
        </DialogActions>
      ) : null}
    </Dialog>
  );
}

function RowMenu({
  row,
  onView,
  onEdit,
  onDelete,
}) {
  const [
    anchor,
    setAnchor,
  ] = useState(null);

  const choose = (
    handler,
  ) => {
    setAnchor(null);

    handler(row);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(
          event,
        ) =>
          setAnchor(
            event.currentTarget,
          )
        }
      >
        <MoreVertRoundedIcon
          sx={{
            fontSize: 17,
          }}
        />
      </IconButton>

      <Menu
        anchorEl={
          anchor
        }
        open={Boolean(
          anchor,
        )}
        onClose={() =>
          setAnchor(null)
        }
        PaperProps={{
          sx: {
            borderRadius: 0,
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() =>
            choose(
              onView,
            )
          }
          sx={{
            gap: 1,
            fontSize:
              "0.7rem",
          }}
        >
          <VisibilityOutlinedIcon
            sx={{
              fontSize: 16,
            }}
          />

          View details
        </MenuItem>

        <PermissionGate permission="update_shop_wifi_rules">
          <MenuItem
            onClick={() =>
              choose(
                onEdit,
              )
            }
            sx={{
              gap: 1,

              fontSize:
                "0.7rem",
            }}
          >
            <EditOutlinedIcon
              sx={{
                fontSize: 16,
              }}
            />

            Edit rule
          </MenuItem>
        </PermissionGate>

        <PermissionGate permission="delete_shop_wifi_rules">
          <MenuItem
            onClick={() =>
              choose(
                onDelete,
              )
            }
            sx={{
              gap: 1,

              color:
                "error.main",

              fontSize:
                "0.7rem",
            }}
          >
            <DeleteOutlineRoundedIcon
              sx={{
                fontSize: 16,
              }}
            />

            Delete rule
          </MenuItem>
        </PermissionGate>
      </Menu>
    </>
  );
}

export function ShopWifiPackageRulesPage() {
  const queryClient =
    useQueryClient();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const {
    shopId,
  } = useParams();

  /*
   * Shop passed from ShopsPage.
   *
   * The URL shopId remains the
   * authoritative ID used for
   * all API writes.
   */
  const selectedShop =
    location.state
      ?.shop ??
    null;

  const [
    searchInput,
    setSearchInput,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: 20,
  });

  const [
    dialog,
    setDialog,
  ] = useState({
    type: null,
    rule: null,
  });

  const [
    message,
    setMessage,
  ] = useState(null);

  useEffect(() => {
    const timer =
      window.setTimeout(
        () => {
          setSearch(
            searchInput.trim(),
          );

          setPaginationModel(
            (
              current,
            ) => ({
              ...current,

              page: 0,
            }),
          );
        },
        450,
      );

    return () =>
      window.clearTimeout(
        timer,
      );
  }, [
    searchInput,
  ]);

  const packagesQuery =
    useQuery({
      queryKey: [
        "rule-package-options",
      ],

      queryFn: () =>
        getWifiPackages({
          page: 1,

          per_page:
            100,

          is_active:
            1,
        }),

      staleTime:
        5 *
        60 *
        1000,
    });

  const rulesQuery =
    useQuery({
      queryKey: [
        "shop-wifi-package-rules",
        shopId,
        search,
        paginationModel.page,
        paginationModel.pageSize,
      ],

      queryFn: () =>
        getShopWifiPackageRules(
          {
            shop_id:
              shopId,

            search:
              search ||
              undefined,

            page:
              paginationModel.page +
              1,

            per_page:
              paginationModel.pageSize,
          },
        ),

      enabled:
        Boolean(
          shopId,
        ),

      placeholderData:
        (
          previous,
        ) =>
          previous,
    });

  const packages =
    extractRows(
      packagesQuery.data,
      "packages",
    );

  const rawRows =
    extractRows(
      rulesQuery.data,
      "rules",
    );

  /*
   * Extra frontend protection:
   * even if backend filtering is
   * accidentally missing, do not
   * render another shop's rules.
   */
  const rows =
    useMemo(
      () =>
        rawRows.filter(
          (
            rule,
          ) =>
            String(
              rule.shop_id ??
                rule.shop
                  ?.id ??
                "",
            ) ===
            String(
              shopId ??
                "",
            ),
        ),

      [
        rawRows,
        shopId,
      ],
    );

  /*
   * Since this screen is scoped
   * to the selected shop, use
   * filtered count as a fallback.
   */
  const apiTotal =
    extractTotal(
      rulesQuery.data,
      "rules",
    );

  const total =
    rows.length <
      rawRows.length
      ? rows.length
      : apiTotal;

  const detailsQuery =
    useQuery({
      queryKey: [
        "shop-wifi-package-rule",
        dialog.rule
          ?.id,
      ],

      queryFn: () =>
        getShopWifiPackageRule(
          dialog.rule.id,
        ),

      enabled:
        dialog.type ===
          "view" &&
        Boolean(
          dialog.rule
            ?.id,
        ),
    });

  const detailRule =
    detailsQuery.data
      ?.rule ??
    detailsQuery.data
      ?.data
      ?.rule ??
    dialog.rule;

  /*
   * Single create/update.
   *
   * shop_id comes ONLY from URL.
   * The form cannot choose it.
   */
  const saveMutation =
    useMutation({
      mutationFn: ({
        rule,
        values,
      }) => {
        if (!shopId) {
          throw new Error(
            "Shop ID is missing.",
          );
        }

        const payload = {
          ...values,

          shop_id:
            shopId,

          minimum_amount:
            Number(
              values.minimum_amount,
            ),

          currency:
            String(
              values.currency,
            ).toUpperCase(),

          is_active:
            Boolean(
              values.is_active,
            ),
        };

        return rule
          ? updateShopWifiPackageRule(
              rule.id,
              payload,
            )
          : createShopWifiPackageRule(
              payload,
            );
      },

      onSuccess: (
        response,
        variables,
      ) => {
        queryClient.invalidateQueries(
          {
            queryKey: [
              "shop-wifi-package-rules",
            ],
          },
        );

        setDialog({
          type: null,
          rule: null,
        });

        setMessage({
          title:
            variables.rule
              ? "Rule updated"
              : "Rule created",

          text:
            response?.message ||
            "Shop Wi-Fi rule saved successfully.",
        });
      },

      onError: (
        error,
      ) =>
        setMessage({
          title:
            "Unable to save rule",

          text:
            getApiErrorMessage(
              error,
            ),
        }),
    });

  /*
   * Bulk create.
   *
   * Every rule is forced to the
   * shop from the current URL.
   */
  const bulkMutation =
    useMutation({
      mutationFn:
        async (
          rules,
        ) => {
          if (
            !shopId
          ) {
            throw new Error(
              "Shop ID is missing.",
            );
          }

          const results =
            [];

          for (
            const rule of
            rules
          ) {
            const payload =
              {
                ...rule,

                shop_id:
                  shopId,

                minimum_amount:
                  Number(
                    rule.minimum_amount,
                  ),

                currency:
                  String(
                    rule.currency,
                  ).toUpperCase(),

                is_active:
                  Boolean(
                    rule.is_active ??
                      true,
                  ),
              };

            results.push(
              await createShopWifiPackageRule(
                payload,
              ),
            );
          }

          return results;
        },

      onSuccess: (
        results,
      ) => {
        queryClient.invalidateQueries(
          {
            queryKey: [
              "shop-wifi-package-rules",
            ],
          },
        );

        setDialog({
          type: null,
          rule: null,
        });

        setMessage({
          title:
            "Rules created",

          text: `${
            results.length
          } rule${
            results.length ===
            1
              ? ""
              : "s"
          } created successfully.`,
        });
      },

      onError: (
        error,
      ) =>
        setMessage({
          title:
            "Bulk creation stopped",

          text: `${getApiErrorMessage(
            error,
          )} Rules submitted before the failure may already have been saved.`,
        }),
    });

  const deleteMutation =
    useMutation({
      mutationFn: (
        id,
      ) =>
        deleteShopWifiPackageRule(
          id,
        ),

      onSuccess: (
        response,
      ) => {
        queryClient.invalidateQueries(
          {
            queryKey: [
              "shop-wifi-package-rules",
            ],
          },
        );

        setDialog({
          type: null,
          rule: null,
        });

        setMessage({
          title:
            "Rule deleted",

          text:
            response?.message ||
            "Rule deleted successfully.",
        });
      },

      onError: (
        error,
      ) =>
        setMessage({
          title:
            "Unable to delete rule",

          text:
            getApiErrorMessage(
              error,
            ),
        }),
    });

  const columns =
    useMemo(
      () => [
        {
          field:
            "avatar",

          headerName:
            "",

          width:
            54,

          sortable:
            false,

          filterable:
            false,

          renderCell: (
            params,
          ) => (
            <Avatar
              sx={{
                width: 28,

                height: 28,

                bgcolor:
                  "#EEF3FF",

                color:
                  "#2F5BEA",

                fontSize:
                  "0.6rem",

                fontWeight:
                  850,
              }}
            >
              {initials(
                selectedShop
                  ?.shop_name ||
                  params.row
                    .shop
                    ?.shop_name ||
                  "WR",
              )}
            </Avatar>
          ),
        },

        {
          field:
            "wifi_package",

          headerName:
            "Wi-Fi package",

          minWidth:
            220,

          flex: 1,

          valueGetter: (
            _,
            row,
          ) =>
            row
              .wifi_package
              ?.name ||
            "—",
        },

        {
          field:
            "minimum_amount",

          headerName:
            "Minimum amount",

          minWidth:
            160,

          valueGetter: (
            _,
            row,
          ) =>
            money(
              row.minimum_amount,
              row.currency,
            ),
        },

        {
          field:
            "currency",

          headerName:
            "Currency",

          width:
            100,
        },

        {
          field:
            "status",

          headerName:
            "Status",

          width:
            100,

          renderCell: (
            params,
          ) => {
            const active =
              params.row
                .is_active ??
              true;

            return (
              <Chip
                label={
                  active
                    ? "Active"
                    : "Inactive"
                }
                size="small"
                sx={{
                  height:
                    22,

                  borderRadius:
                    0,

                  bgcolor:
                    active
                      ? "#E7F6EE"
                      : "#F1F3F5",

                  color:
                    active
                      ? "#18794E"
                      : "#667085",

                  fontSize:
                    "0.65rem",

                  fontWeight:
                    750,
                }}
              />
            );
          },
        },

        {
          field:
            "actions",

          headerName:
            "",

          width:
            54,

          sortable:
            false,

          filterable:
            false,

          renderCell: (
            params,
          ) => (
            <RowMenu
              row={
                params.row
              }
              onView={(
                rule,
              ) =>
                setDialog(
                  {
                    type: "view",

                    rule,
                  },
                )
              }
              onEdit={(
                rule,
              ) =>
                setDialog(
                  {
                    type: "edit",

                    rule,
                  },
                )
              }
              onDelete={(
                rule,
              ) =>
                setDialog(
                  {
                    type: "delete",

                    rule,
                  },
                )
              }
            />
          ),
        },
      ],

      [
        selectedShop,
      ],
    );

  const shopName =
    selectedShop
      ?.shop_name ||
    rawRows[0]?.shop
      ?.shop_name ||
    "Shop";

  const shopCode =
    selectedShop
      ?.shop_code ||
    rawRows[0]?.shop
      ?.shop_code ||
    "";

  const shopForForm =
    selectedShop ??
    rawRows[0]?.shop ??
    {
      id:
        shopId,

      shop_name:
        shopName,

      shop_code:
        shopCode,
    };

  return (
    <Box
      sx={{
        p: {
          xs: 1.2,
          md: 2,
        },
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 0,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          spacing={1}
          sx={{
            p: 1.2,

            borderBottom:
              "1px solid #E3E7EC",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flex: 1,
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              startIcon={
                <ArrowBackRoundedIcon
                  sx={{
                    fontSize:
                      17,
                  }}
                />
              }
              onClick={() =>
                navigate(
                  "/shops",
                )
              }
              sx={{
                minHeight:
                  36,

                borderRadius:
                  0,

                whiteSpace:
                  "nowrap",

                fontSize:
                  "0.68rem",

                fontWeight:
                  750,
              }}
            >
              Back to shops
            </Button>

            <Box>
              <Typography
                sx={{
                  fontSize:
                    "0.82rem",

                  fontWeight:
                    850,
                }}
              >
                Wi-Fi rule management
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,

                  color:
                    "#667085",

                  fontSize:
                    "0.66rem",
                }}
              >
                {shopName}

                {shopCode
                  ? ` · ${shopCode}`
                  : ""}
              </Typography>
            </Box>
          </Stack>

          <TextField
            size="small"
            value={
              searchInput
            }
            onChange={(
              event,
            ) =>
              setSearchInput(
                event.target
                  .value,
              )
            }
            placeholder="Search package or currency"
            sx={{
              width: {
                xs: "100%",
                md: 280,
              },

              "& .MuiInputBase-root":
                {
                  height: 36,

                  borderRadius:
                    0,

                  fontSize:
                    "0.7rem",
                },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{
                      fontSize:
                        16,
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <PermissionGate permission="create_shop_wifi_rules">
            <Button
              variant="outlined"
              startIcon={
                <PlaylistAddRoundedIcon />
              }
              onClick={() =>
                setDialog(
                  {
                    type: "bulk",

                    rule: null,
                  },
                )
              }
              sx={{
                minHeight:
                  36,

                borderRadius:
                  0,

                fontSize:
                  "0.68rem",
              }}
            >
              Add many
            </Button>
          </PermissionGate>

          <PermissionGate permission="create_shop_wifi_rules">
            <Button
              variant="contained"
              startIcon={
                <AddRoundedIcon />
              }
              onClick={() =>
                setDialog(
                  {
                    type: "create",

                    rule: null,
                  },
                )
              }
              sx={{
                minHeight:
                  36,

                borderRadius:
                  0,

                fontSize:
                  "0.68rem",
              }}
            >
              New rule
            </Button>
          </PermissionGate>
        </Stack>

        <SoftDataGrid
          rows={rows}
          columns={
            columns
          }
          loading={
            rulesQuery.isLoading
          }
          rowCount={
            total
          }
          paginationMode="server"
          paginationModel={
            paginationModel
          }
          onPaginationModelChange={
            setPaginationModel
          }
          pageSizeOptions={[
            10,
            20,
            50,
            100,
          ]}
          getRowId={(
            row,
          ) =>
            row.id
          }
        />
      </Paper>

      {/* CREATE / EDIT */}

      <SquareDialog
        open={
          dialog.type ===
            "create" ||
          dialog.type ===
            "edit"
        }
        onClose={() =>
          setDialog({
            type: null,
            rule: null,
          })
        }
        title={
          dialog.type ===
          "edit"
            ? "Edit shop Wi-Fi rule"
            : "Create shop Wi-Fi rule"
        }
        subtitle={`Configure Wi-Fi access for ${shopName}${
          shopCode
            ? ` (${shopCode})`
            : ""
        }.`}
      >
        <ShopWifiPackageRuleForm
          key={`${dialog.type}-${
            dialog.rule
              ?.id ||
            "new"
          }`}
          rule={
            dialog.type ===
            "edit"
              ? dialog.rule
              : null
          }
          shop={
            shopForForm
          }
          packages={
            packages
          }
          busy={
            saveMutation.isPending
          }
          onCancel={() =>
            setDialog({
              type: null,
              rule: null,
            })
          }
          onSubmit={(
            values,
          ) =>
            saveMutation.mutate(
              {
                rule:
                  dialog.type ===
                  "edit"
                    ? dialog.rule
                    : null,

                values,
              },
            )
          }
        />
      </SquareDialog>

      {/* BULK CREATE */}

      <SquareDialog
        open={
          dialog.type ===
          "bulk"
        }
        onClose={() =>
          setDialog({
            type: null,
            rule: null,
          })
        }
        title="Create many Wi-Fi rules"
        subtitle={`All rules will be created for ${shopName}${
          shopCode
            ? ` (${shopCode})`
            : ""
        }.`}
      >
        <BulkShopWifiPackageRulesForm
          shop={
            shopForForm
          }
          packages={
            packages
          }
          busy={
            bulkMutation.isPending
          }
          onCancel={() =>
            setDialog({
              type: null,
              rule: null,
            })
          }
          onSubmit={(
            rules,
          ) =>
            bulkMutation.mutate(
              rules,
            )
          }
        />
      </SquareDialog>

      {/* VIEW */}

      <SquareDialog
        open={
          dialog.type ===
          "view"
        }
        onClose={() =>
          setDialog({
            type: null,
            rule: null,
          })
        }
        title="Rule details"
        actions={
          <Button
            onClick={() =>
              setDialog(
                {
                  type: null,

                  rule: null,
                },
              )
            }
            variant="contained"
            sx={{
              borderRadius:
                0,
            }}
          >
            Close
          </Button>
        }
      >
        {detailRule ? (
          <Stack
            spacing={1}
          >
            <Typography
              sx={{
                fontSize:
                  "0.9rem",

                fontWeight:
                  850,
              }}
            >
              {shopName}
            </Typography>

            {shopCode ? (
              <Typography
                sx={{
                  fontSize:
                    "0.7rem",
                }}
              >
                Shop code:{" "}
                {shopCode}
              </Typography>
            ) : null}

            <Typography
              sx={{
                fontSize:
                  "0.7rem",
              }}
            >
              Package:{" "}
              {detailRule
                .wifi_package
                ?.name ||
                "—"}
            </Typography>

            <Typography
              sx={{
                fontSize:
                  "0.7rem",
              }}
            >
              Minimum amount:{" "}
              {money(
                detailRule.minimum_amount,
                detailRule.currency,
              )}
            </Typography>

            <Typography
              sx={{
                fontSize:
                  "0.7rem",
              }}
            >
              Currency:{" "}
              {detailRule.currency ||
                "—"}
            </Typography>

            <Typography
              sx={{
                fontSize:
                  "0.7rem",
              }}
            >
              Status:{" "}
              {detailRule.is_active
                ? "Active"
                : "Inactive"}
            </Typography>
          </Stack>
        ) : null}
      </SquareDialog>

      {/* DELETE */}

      <SquareDialog
        open={
          dialog.type ===
          "delete"
        }
        onClose={() =>
          setDialog({
            type: null,
            rule: null,
          })
        }
        title="Delete shop Wi-Fi rule"
        subtitle="This action cannot be undone."
        maxWidth="sm"
        actions={
          <>
            <Button
              color="inherit"
              onClick={() =>
                setDialog(
                  {
                    type: null,

                    rule: null,
                  },
                )
              }
              sx={{
                borderRadius:
                  0,
              }}
            >
              Cancel
            </Button>

            <Button
              color="error"
              variant="contained"
              disabled={
                deleteMutation.isPending
              }
              onClick={() =>
                deleteMutation.mutate(
                  dialog.rule
                    .id,
                )
              }
              sx={{
                borderRadius:
                  0,
              }}
            >
              {deleteMutation.isPending
                ? "Deleting..."
                : "Delete rule"}
            </Button>
          </>
        }
      >
        <Typography
          sx={{
            fontSize:
              "0.72rem",
          }}
        >
          Delete the{" "}
          <strong>
            {dialog.rule
              ?.wifi_package
              ?.name ||
              "Wi-Fi"}
          </strong>{" "}
          rule for{" "}
          <strong>
            {shopName}
          </strong>
          ?
        </Typography>
      </SquareDialog>

      {/* MESSAGE */}

      <Dialog
        open={Boolean(
          message,
        )}
        onClose={() =>
          setMessage(null)
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize:
              "0.82rem",

            fontWeight:
              850,
          }}
        >
          {message?.title}
        </DialogTitle>

        <DialogContent
          dividers
        >
          <Typography
            sx={{
              fontSize:
                "0.7rem",

              lineHeight:
                1.6,
            }}
          >
            {message?.text}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() =>
              setMessage(
                null,
              )
            }
            sx={{
              borderRadius:
                0,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}